import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

const faqs = [
  {
    q: "Is my journal actually private?",
    a: "Yes. Entries are encrypted at rest and only you can read them. We never train AI on your words, never sell data, and no one on our team reads individual entries. Export or delete anytime.",
  },
  {
    q: "Does this replace my pastor, spiritual director, or church?",
    a: "No. Eden Pages is a practice tool, not a relationship. It's built to work alongside your church community — the way a good workbook or a spiritual director's prompt book might.",
  },
  {
    q: "Will the AI say things I disagree with theologically?",
    a: "The companion stays within the wide historic consensus of Christian tradition. You can choose your translation (Deuterocanon included for Catholic and Orthodox), and each persona avoids sectarian positions on contested doctrine.",
  },
  {
    q: "What happens after my 10-day trial?",
    a: "Nothing automatic. No credit card means no charge and no cancellation. On day 11 you choose: stay on the free Explorer plan, or upgrade to Devoted for $7/month or $84/year.",
  },
  {
    q: "Who's behind Eden Pages?",
    a: "A small team of Christians building the devotional tool we kept wishing existed. Eden Pages is not affiliated with any denomination, seminary, or church body. More in the founder's note below.",
  },
  {
    q: "Is this just ChatGPT with a Bible wrapper?",
    a: "No. Scripture comes from vector search across the full Bible — not generated by AI. Every verse is chosen based on what you actually wrote. The AI only shapes the reflection, never invents a passage.",
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section
      id="faq"
      className="py-[120px] px-6 sm:px-10"
      style={{ background: C.warm }}
    >
      <div className="max-w-[680px] mx-auto">

        <FadeIn>
          <div className="text-center" style={{ marginBottom: 64 }}>
            <p
              className="font-ui"
              style={{
                fontSize: 11,
                color: C.txt3,
                letterSpacing: "1.4px",
                marginBottom: 20,
              }}
            >
              COMMON QUESTIONS
            </p>
            <h2
              className="font-heading font-semibold text-spec-brown text-center"
              style={{
                fontSize: "clamp(34px, 4.2vw, 52px)",
                lineHeight: 1.12,
              }}
            >
              What people usually
              <br />
              <em className="italic text-spec-sage">want to ask first.</em>
            </h2>
          </div>
        </FadeIn>

        <div>
          {faqs.map((item, i) => {
            const isOpen = openIdx === i;
            const isLast = i === faqs.length - 1;
            return (
              <div
                key={i}
                style={{
                  borderTop: `1px solid ${C.bdr}`,
                  ...(isLast ? { borderBottom: `1px solid ${C.bdr}` } : {}),
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full flex items-center justify-between cursor-pointer"
                  style={{
                    gap: 24,
                    padding: "24px 0",
                    background: "transparent",
                    border: "none",
                    textAlign: "left",
                  }}
                  aria-expanded={isOpen}
                >
                  <span
                    className="font-heading font-semibold"
                    style={{
                      color: C.txt,
                      fontSize: "clamp(17px, 1.9vw, 20px)",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.q}
                  </span>
                  {isOpen ? (
                    <Minus
                      size={18}
                      color={C.sage}
                      style={{ flexShrink: 0 }}
                    />
                  ) : (
                    <Plus
                      size={18}
                      color={C.sage}
                      style={{ flexShrink: 0 }}
                    />
                  )}
                </button>
                {isOpen && (
                  <div
                    className="font-ui"
                    style={{
                      padding: "0 0 28px 0",
                      fontSize: 15,
                      lineHeight: 1.8,
                      color: C.txt2,
                      maxWidth: 560,
                      animation: "edenFaqFade 0.25s ease",
                    }}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <FadeIn delay={0.2}>
          <p
            className="font-ui text-center"
            style={{
              marginTop: 48,
              fontSize: 14,
              color: C.txt3,
            }}
          >
            Have a different question? Email{" "}
            <a
              href="mailto:hello@edenpages.com"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.sage, textDecoration: "none" }}
            >
              hello@edenpages.com
            </a>{" "}
            — we read every one.
          </p>
        </FadeIn>

      </div>

      <style>{`
        @keyframes edenFaqFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
